import React from 'react';
import BaseClass from 'components/BaseClass';
import LayoutCard from 'components/FrictionlessCheckout/LayoutCard/LayoutCard';
import { wrapComponent } from 'utils/framework';
import {
    Box, Text, Icon, Flex
} from 'components/ui';
import PaymentsDisplay from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentsDisplay/PaymentsDisplay';
import PaymentMethodList from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList';
import NewUserPaymentView from 'components/FrictionlessCheckout/PaymentReleaseFour/NewUserPaymentView';
import CreditCardArea from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard/CreditCardArea';
import { AfterPayExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AfterPay';
import { KlarnaExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Klarna';
import { VenmoExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo';
import PazeExpandedView from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paze/PazeExpandedView';
import anaConsts from 'analytics/constants';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import StringUtils from 'utils/String';
import { SECTION_NAMES, FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH } from 'constants/frictionlessCheckout';
import OrderUtils from 'utils/Order';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import Venmo from 'utils/Venmo';
import agentAwareUtils from 'utils/AgentAware';
import checkoutUtils from 'utils/Checkout';
import deepEqual from 'deep-equal';
import { createKlarnaWidgetLoader } from 'components/FrictionlessCheckout/PaymentReleaseFour/utils/AlternatePaymentWidgetHelpers';
import afterpayUtils from 'utils/Afterpay';
import ApplePay from 'services/ApplePay';
import Empty from 'constants/empty';
import PayPal from 'utils/PayPal';
import Location from 'utils/Location';
import { refreshCheckout } from 'services/Checkout';

class Payment extends BaseClass {
    state = {
        isHigherRedeemPoints: this.props.zeroDollarHigherRedeemPointsNotCVV || this.props.zeroDollarHigherRedeemPointsCVV,
        openCreditCardForm: false,
        shouldAutoExpandAfterpay: false,
        isAfterPayExpanded: false,
        isAfterPayLoading: false,
        afterpayApiData: null,
        afterpayError: null,
        shouldAutoExpandKlarna: false,
        isKlarnaExpanded: false,
        isKlarnaLoading: false,
        klarnaApiData: null,
        klarnaError: null,
        useMyShippingAddress: true,
        shouldAutoExpandPayPal: false,
        shouldAutoExpandPaze: false,
        shouldAutoExpandVenmo: false,
        previousPaymentSelection: null,
        isGiftCardFormOpenOnL1: false,
        suppressAutoOpenAfterExternalExit: false
    };

    isNewUserWithVenmoExpress = false;
    didHandleVenmoExpressAutoClose = false;

    // Stop handlePaymentCheckOnExitEditView from resetting the alternate payment
    // after Save/Cancel since props may still be stale during isEditViewFromURL update.
    exitingViaCloseEditState = false;

    // Tracks if the user clicked "Change" to enter edit mode (vs entering edit mode automatically, like after saving an address).
    userInitiatedEditMode = false;

    setEditChangeLink = (isEditMode, isTriggeredByUserClick = false) => {
        if (isEditMode) {
            if (this.state.suppressAutoOpenAfterExternalExit) {
                this.setState({ suppressAutoOpenAfterExternalExit: false });
            }

            this.userInitiatedEditMode = true;
            this.props.getCreditCards(this.props.orderId);
            FrictionlessCheckoutBindings.setChangeLinkAnalytics(anaConsts.PAGE_TYPES.PAYMENT, isTriggeredByUserClick);
            this.props.goToURL({ path: `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.PAYMENT.path}` });
            this.props.setPaymentMethodNeedsConfirmation(true);
        }
    };

    handleAddNewCard = () => {
        this.setState({ openCreditCardForm: true });
    };

    handleCloseCreditCardForm = () => {
        this.setState({ openCreditCardForm: false });
    };

    handleNewUserCreditCardSave = () => {
        this.handleCloseCreditCardForm();
        this.closeEditState();
    };

    /**
     * When user clicks Afterpay pill in L1 (PaymentsDisplay) or NewUserPaymentView,
     * opens the Payment edit view (L2) and triggers auto-expansion to L3.
     * This handles AfterPay expansion at the Payment level to work for all user types.
     */
    handleAfterPayExpandFromL1 = () => {
        // Navigate to edit view first
        this.setEditChangeLink(true, true);

        // Then trigger AfterPay expansion
        this.handleAfterPayExpand();
    };

    /**
     * Launches PayPal directly from L1 without switching to L2.
     * This keeps users on the same level if they cancel the PayPal popup.
     */
    handlePayPalExpandFromL1 = () => {
        if (OrderUtils.containsRestrictedItem()) {
            this.props.setCheckoutSectionErrors({ paymentMethod: this.props.localization.paypalRestrictedItemError });

            return;
        }

        PayPal.showPayPal(payload => {
            this.props.payPalWithInterstice(payload).then(() => {
                this.props.commonOrderToggleActions?.();
                this.props.updatePaymentActions?.();
                this.props.clearPendingCreditCard?.();
                this.props.sectionSaveOrderAction?.(Location.getLocation().pathname, this);
                refreshCheckout()();
            });
        });
    };

    /**
     * Opens Payment edit view (L2) then triggers Paze L3 expansion.
     * Called when user clicks Paze pill from L1 state.
     */
    handlePazeExpandFromL1 = () => {
        this.setEditChangeLink(true, true);
        this.setState({ shouldAutoExpandPaze: true });
    };

    handleGiftCardFormVisibilityOnL1 = isVisible => {
        this.setState({ isGiftCardFormOpenOnL1: isVisible });
    };

    /**
     * Handles Apple Pay click from NewUserPaymentView (guest/new user path).
     */
    handlePayWithApplePayClick = () => {
        if (checkoutUtils.isGuestOrder()) {
            ApplePay.enableGuestCheckout();
        }

        ApplePay.onApplePayClicked();
    };

    /**
     * Opens Venmo expanded view from NewUserPaymentView (guest/new user path).
     */
    handleVenmoExpandFromL1 = () => {
        Venmo.prepareVenmoCheckout();
        this.setState({ shouldAutoExpandVenmo: true });
    };

    /**
     * Triggered when user clicks "Save and Continue" in Venmo expanded view.
     * Selects Venmo and returns to L1 state.
     */
    handleVenmoSaveAndContinue = () => {
        Venmo.initializeVenmoCheckout();
        this.props.commonOrderToggleActions && this.props.commonOrderToggleActions(false, false, false, true);
        this.props.updatePaymentActions && this.props.updatePaymentActions(false, false);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();
        this.setEditChangeLink(false);
        this.setState({ shouldAutoExpandVenmo: false });
        this.closeEditState();
    };

    /**
     * Triggered when user clicks "Cancel" in Venmo expanded view.
     * Collapses the L3 expanded view without selecting Venmo.
     * Returns user to L2 edit mode.
     */
    clearAutoExpandVenmo = () => {
        this.setState({ shouldAutoExpandVenmo: false });
    };

    // Triggered when user clicks "Save and Continue" in Paze expanded view aka L3.
    // Selects Paze and returns to L1 state.

    handlePazeSaveAndContinue = () => {
        this.props.loadPaze && this.props.loadPaze({});
        this.props.commonOrderToggleActions && this.props.commonOrderToggleActions(false, false, false, false);
        this.props.updatePaymentActions && this.props.updatePaymentActions(true, false);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();
        this.setState({ shouldAutoExpandPaze: false });
        this.closeEditState();
    };

    // Collapses back to L2 without selecting Paze.
    handlePazeCancel = () => {
        this.setState({ shouldAutoExpandPaze: false });
    };

    /**
     * Shows the AfterPay expanded view. The actual widget loading is triggered
     * by AfterPayExpandedView after mount (to ensure DOM element exists).
     */
    handleAfterPayExpand = async () => {
        const widgetElementId = 'afterpay-widget-expanded-view';

        // Store current payment state before expanding
        // This allows Cancel to restore previous state
        const {
            alternateMethodSelected, alternatePaymentName, klarna, afterpay, selectedAsDefaultPaymentName
        } = this.props;

        this.setState({
            previousPaymentSelection: {
                alternateMethodSelected,
                alternatePaymentName,
                klarnaSelected: klarna?.isSelected,
                afterpaySelected: afterpay?.isSelected,
                selectedAsDefaultPaymentName
            },
            isAfterPayExpanded: true,
            isAfterPayLoading: true,
            afterpayError: null,
            afterpayApiData: { widgetElementId }
        });

        // track expansion and async page load for L3 state
        FrictionlessCheckoutBindings.setAfterpayExpandedViewAnalytics && FrictionlessCheckoutBindings.setAfterpayExpandedViewAnalytics();
    };

    /**
     * Callback for AfterPayExpandedView to load the widget after mount
     * This ensures the DOM element exists before calling the SDK
     */
    loadAfterPayWidget = async () => {
        // Persist AfterpayPaymentGroup to the backend so it survives page reload.
        try {
            const order = await afterpayUtils.initPaymentGroup();
            this.props.dispatchOrderUpdate(order);
        } catch (error) {
            Sephora.logger.error('[Afterpay] initPaymentGroup failed:', error);
        }

        const { orderDetails, loadExpandedViewWidget } = this.props;
        const priceInfo = orderDetails?.priceInfo;
        // Try multiple fields to get the order amount bc the SDK requires a positive amount
        const orderTotal = priceInfo?.creditCardAmount || priceInfo?.paypalAmount || priceInfo?.orderTotal || 0;
        const widgetElementId = 'afterpay-widget-expanded-view';

        // Afterpay SDK requires a positive amount, show error if amount is 0 or invalid
        if (!orderTotal || orderTotal === '$0.00' || orderTotal === '0') {
            this.setState({
                isAfterPayLoading: false,
                afterpayError: 'Unable to calculate payment schedule. Please try again.'
            });

            return;
        }

        if (loadExpandedViewWidget) {
            // Set a timeout fallback in case SDK never calls onReady
            const timeoutId = setTimeout(() => {
                this.setState({ isAfterPayLoading: false });
            }, 10000); // 10 second timeout

            try {
                await loadExpandedViewWidget(widgetElementId, orderTotal);
                clearTimeout(timeoutId);
                this.setState({ isAfterPayLoading: false });
            } catch (error) {
                clearTimeout(timeoutId);
                this.setState({
                    isAfterPayLoading: false,
                    afterpayError: error?.message || 'Unable to load Afterpay information. Please try again.'
                });
            }
        } else {
            this.setState({ isAfterPayLoading: false });
        }
    };

    /**
     * Collapses L3, selects Afterpay, returns to L1.
     * This is the ONLY place where Afterpay gets selected (user confirmed).
     */
    handleAfterPaySaveAndContinue = () => {
        // Select Afterpay in Redux (user confirmed choice)
        this.props.commonOrderToggleActions && this.props.commonOrderToggleActions(false, false, true, false);
        this.props.afterpaySetReady(true);
        this.props.setPaymentMethodAfterPay(this.props.localization.afterpayErrorMessage);
        this.props.clearPendingCreditCard();

        this.setState({
            isAfterPayExpanded: false,
            afterpayApiData: null,
            previousPaymentSelection: null
        });

        this.closeEditState();
    };

    /**
     * Reverts "Set as default payment" if user checked it but then clicked Cancel.
     * Works for any payment method (Afterpay, Klarna, Venmo, PayPal, etc.)
     * @param {string} paymentMethodName - Payment method name (e.g., 'afterpay', 'klarna', 'venmo')
     */
    revertDefaultPaymentIfChanged = paymentMethodName => {
        const currentSelectedAsDefault = this.props.selectedAsDefaultPaymentName;
        const previousSelectedAsDefault = this.state.previousPaymentSelection?.selectedAsDefaultPaymentName;

        if (currentSelectedAsDefault === paymentMethodName && previousSelectedAsDefault !== paymentMethodName) {
            // User set payment method as default but then canceled - remove from backend
            this.props.removeDefaultPaymentFromProfile(paymentMethodName);
        }
    };

    /**
     * Collapses L3 without selecting Afterpay.
     * For new users: returns to L2 (NewUserPaymentView).
     * For existing users: returns to L1 (collapsed state).
     */
    handleAfterPayCancel = () => {
        const hasNoPayments = this.hasNoSavedPaymentMethods();
        const { previousPaymentSelection } = this.state;

        // Revert default payment if user changed it but then canceled
        // We have to call this before clearing previousPaymentSelection state
        this.revertDefaultPaymentIfChanged('afterpay');

        // Restore or clear Afterpay selection based on previous state
        const afterpayWasPreviouslySelected = previousPaymentSelection?.afterpaySelected === true;

        if (afterpayWasPreviouslySelected) {
            this.props.commonOrderToggleActions && this.props.commonOrderToggleActions(false, false, true, false);
            this.props.afterpaySetReady && this.props.afterpaySetReady(true);

            // Now safe to clear UI state, afterpay selection is already restored in Redux
            this.setState({
                isAfterPayExpanded: false,
                afterpayApiData: null,
                afterpayError: null,
                previousPaymentSelection: null
            });

            // Always return to L1, the restored Afterpay is a valid payment
            this.closeEditState();
        } else {
            // Afterpay wasn't selected before, deselect it, preserve other selections
            this.props.afterpaySetReady && this.props.afterpaySetReady(false);
            const klarnaStillSelected = previousPaymentSelection?.klarnaSelected || false;

            // commonOrderToggleActions(mergeValue, KlarnaValue, afterPayValue, venmoValue)
            this.props.commonOrderToggleActions &&
                this.props.commonOrderToggleActions(
                    false, // mergeValue (isApplePayFlow)
                    klarnaStillSelected, // preserve Klarna
                    false, // Afterpay = false
                    false // Venmo
                );

            // Clear UI state
            this.setState({
                isAfterPayExpanded: false,
                afterpayApiData: null,
                afterpayError: null,
                previousPaymentSelection: null
            });

            // Navigation logic: new users stay in L2, existing users return to L1
            if (!hasNoPayments) {
                this.closeEditState();
            }
        }
    };

    /**
     * Callback from PaymentMethodList to clear the auto-expand flag
     * after AfterPay expansion has been triggered.
     */
    clearAutoExpandAfterpay = () => {
        this.setState({ shouldAutoExpandAfterpay: false });
    };

    /**
     * Opens Payment edit view (L2) then triggers Klarna L3 expansion.
     * Called when user clicks Klarna pill from L1 state.
     */
    handleKlarnaExpandFromL1 = () => {
        this.setEditChangeLink(true, true);
        this.handleKlarnaExpand();
    };

    /**
     * Shows Klarna expanded view (L3 state).
     * Widget loading happens after mount via KlarnaExpandedView.
     */
    handleKlarnaExpand = async () => {
        const widgetElementId = 'klarna-widget-expanded-view';

        // Store current payment state before expanding
        // This allows Cancel to restore previous state
        const {
            alternateMethodSelected, alternatePaymentName, klarna, afterpay, selectedAsDefaultPaymentName
        } = this.props;

        this.setState({
            previousPaymentSelection: {
                alternateMethodSelected,
                alternatePaymentName,
                klarnaSelected: klarna?.isSelected,
                afterpaySelected: afterpay?.isSelected,
                selectedAsDefaultPaymentName
            },
            isKlarnaExpanded: true,
            isKlarnaLoading: true,
            klarnaError: null,
            klarnaApiData: { widgetElementId }
        });

        FrictionlessCheckoutBindings.setKlarnaExpandedViewAnalytics && FrictionlessCheckoutBindings.setKlarnaExpandedViewAnalytics();
    };

    /**
     * Loads Klarna SDK widget after DOM element is ready.
     * Uses shared Util to keep code dry.
     */
    loadKlarnaWidget = async () => {
        const loader = createKlarnaWidgetLoader({
            widgetElementId: 'klarna-widget-expanded-view',
            setState: this.setState.bind(this),
            klarnaSetReady: this.props.klarnaSetReady,
            locales: this.props.localization
        });

        return loader();
    };

    /**
     * Collapses L3, selects Klarna, returns to L1.
     * This is the ONLY place where Klarna gets selected (user confirmed).
     */
    handleKlarnaSaveAndContinue = () => {
        //  Select Klarna in Redux (user confirmed choice)
        this.props.commonOrderToggleActions && this.props.commonOrderToggleActions(false, true, false, false);
        this.props.klarnaSetReady && this.props.klarnaSetReady(true);
        this.props.clearPendingCreditCard();

        this.setState({
            isKlarnaExpanded: false,
            klarnaApiData: null,
            previousPaymentSelection: null
        });

        this.closeEditState();
    };

    /**
     * Collapses L3 without selecting Klarna.
     * For new users: returns to L2 (NewUserPaymentView).
     * For existing users: returns to L1 (collapsed state).
     */
    handleKlarnaCancel = () => {
        const hasNoPayments = this.hasNoSavedPaymentMethods();
        const { previousPaymentSelection } = this.state;

        // Revert default payment if user changed it but then canceled
        this.revertDefaultPaymentIfChanged('klarna');

        // Restore or clear Klarna selection based on previous state
        const klarnaWasPreviouslySelected = previousPaymentSelection?.klarnaSelected === true;

        if (klarnaWasPreviouslySelected) {
            // Klarna was the saved payment, restore it since user canceled.
            this.props.commonOrderToggleActions && this.props.commonOrderToggleActions(false, true, false, false);
            this.props.klarnaSetReady && this.props.klarnaSetReady(true);

            // Now safe to clear UI state — Klarna selection is already restored in Redux
            this.setState({
                isKlarnaExpanded: false,
                klarnaApiData: null,
                klarnaError: null,
                previousPaymentSelection: null
            });

            // Always return to L1, the restored Klarna is a valid payment
            this.closeEditState();
        } else {
            // Klarna wasn't selected before — deselect it, preserve other selections
            this.props.klarnaSetReady && this.props.klarnaSetReady(false);
            const afterpayStillSelected = previousPaymentSelection?.afterpaySelected || false;

            // commonOrderToggleActions(mergeValue, KlarnaValue, afterPayValue, venmoValue)
            this.props.commonOrderToggleActions &&
                this.props.commonOrderToggleActions(
                    false, // mergeValue (isApplePayFlow)
                    false, // Klarna = false
                    afterpayStillSelected, // preserve Afterpay
                    false // Venmo
                );

            // Clear UI state
            this.setState({
                isKlarnaExpanded: false,
                klarnaApiData: null,
                klarnaError: null,
                previousPaymentSelection: null
            });

            // Navigate based on saved payments
            if (!hasNoPayments) {
                this.closeEditState();
            }
        }
    };

    /**
     * Callback from PaymentMethodList to clear the auto-expand flag
     * after Klarna expansion has been triggered.
     */
    clearAutoExpandKlarna = () => {
        this.setState({ shouldAutoExpandKlarna: false });
    };

    /**
     * Clears PayPal auto-expand flag after PaymentMethodList consumes it.
     */
    clearAutoExpandPayPal = () => {
        this.setState({ shouldAutoExpandPayPal: false });
    };

    /**
     * Clears Paze auto-expand flag after PaymentMethodList consumes it.
     */
    clearAutoExpandPaze = () => {
        this.setState({ shouldAutoExpandPaze: false });
    };

    /**
     * Toggles billing address checkbox for Klarna
     */
    handleBillingAddressToggle = () => {
        this.setState(prevState => ({
            useMyShippingAddress: !prevState.useMyShippingAddress
        }));
    };

    componentDidMount() {
        const { orderDetails, tmpCardMessage, gcAmountNotEnoughMessage, alternateMethodSelected } = this.props;

        // Don’t sync Redux with backend if the user already picked an alternate payment.
        // Prevents overwriting Klarna/Afterpay selections due to stale orderDetails.
        if (!alternateMethodSelected) {
            this.props.checkSelectedPayment({ orderDetails });
        }

        if (tmpCardMessage) {
            this.loadTempCardMessageModal(tmpCardMessage);
        }

        if (gcAmountNotEnoughMessage) {
            this.setGiftCardAmountNotEnoughMessage();
        }

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            //If it is Sephora Mirror,for certain roles agent should see payment section open and cvv box opened.

            if (!OrderUtils.isZeroCheckout() && !agentAwareUtils.isAgentAuthorized(['3'])) {
                const updatedOrderDetails = {
                    ...this.props.orderDetails
                };

                // Find and update CreditCardPaymentGroup's isComplete to false
                if (updatedOrderDetails.paymentGroups && Array.isArray(updatedOrderDetails.paymentGroups.paymentGroupsEntries)) {
                    updatedOrderDetails.paymentGroups.paymentGroupsEntries = updatedOrderDetails.paymentGroups.paymentGroupsEntries.map(entry => {
                        if (entry.paymentGroupType === 'CreditCardPaymentGroup' && entry.paymentGroup) {
                            return {
                                ...entry,
                                paymentGroup: {
                                    ...entry.paymentGroup,
                                    isComplete: false
                                }
                            };
                        }

                        return entry;
                    });
                }

                this.props.dispatchOrderUpdate(updatedOrderDetails);
                this.setEditChangeLink(true);
            }
        }

        if (FrictionlessUtils.isCVVMissing(orderDetails.header.paymentGroups)) {
            this.savePendingCreditCard(orderDetails);
        }
    }

    savePendingCreditCard = orderDetails => {
        const creditCardPaymentGroup = OrderUtils.getCreditCardPaymentGroup(orderDetails) || { isComplete: false };
        const creditCardToSave = this.props.paymentOptions.creditCards.filter(
            creditCard => creditCard.creditCardId === creditCardPaymentGroup?.creditCardId
        )?.[0];
        // SET PENDING CC
        this.props.savePendingCreditCard(creditCardToSave);
    };

    setGiftCardAmountNotEnoughMessage = () => {
        this.props.setCheckoutSectionErrors({ Payment: this.props.gcAmountNotEnoughMessage });
    };

    loadTempCardMessageModal = tmpCardMessage => {
        this.props.showInfoModal({
            isOpen: true,
            title: StringUtils.capitalize(tmpCardMessage.type),
            message: tmpCardMessage.messages.join('. ')
        });
    };

    //TODO: will be updated in next stories
    handleAlternatePaymentDetails = () => {
        const { alternatePaymentDetailsModal: { sid, title, width } = Empty.Object, showContentModal, localization } = this.props;

        showContentModal({
            isOpen: true,
            data: {
                sid,
                title,
                width,
                showCloseButton: true,
                closeButtonText: localization.gotIt,
                hasBorderForFooteronLGUI: false,
                hasShadowBorderForSMUI: true,
                isDrawer: true,
                minHeightCloseBtn: 44
            }
        });
    };

    closeEditState = () => {
        this.exitingViaCloseEditState = true;
        this.userInitiatedEditMode = false;
        this.setState({
            isHigherRedeemPoints: false
        });
        this.props.goToURL({ path: FC_CHECKOUT_PATH });
        this.props.clearNamedSectionErrors([SECTION_NAMES.PAYMENT]);
        this.props.setPaymentMethodNeedsConfirmation(false);
    };

    shouldOpenPaymentSection = () => {
        const {
            alternateMethodSelected,
            isPaypalSelected,
            payPalPaymentGroup,
            giftCardPaymentGroups,
            creditCardPaymentGroup,
            isPaymentComplete,
            isShippableOrder,
            hasSavedAddress,
            forcePaymentSectionRender,
            isEditViewFromURL
        } = this.props;
        const { suppressAutoOpenAfterExternalExit } = this.state;

        const hasNoPaymentMethod =
            !suppressAutoOpenAfterExternalExit &&
            !alternateMethodSelected &&
            !isEditViewFromURL &&
            !isPaypalSelected &&
            !payPalPaymentGroup?.isComplete && // If paypayl is complete, no need to open payment section
            !giftCardPaymentGroups.length > 0 &&
            (!creditCardPaymentGroup || !creditCardPaymentGroup.isComplete) &&
            !isPaymentComplete &&
            (!isShippableOrder || hasSavedAddress);

        const shouldOpenForRedeemPoints = !isEditViewFromURL && this.state.isHigherRedeemPoints && !forcePaymentSectionRender;

        return hasNoPaymentMethod || shouldOpenForRedeemPoints;
    };

    shouldCollapsePaymentSection = () => {
        const {
            isEditViewFromURL, alternateMethodSelected, hasSavedAddress, isNewUserFlow, hasNoPaymentOptionSelected
        } = this.props;
        const shouldShowAlternatePayment = alternateMethodSelected && hasSavedAddress;

        // Collapse payment section when user has no saved payment method to display
        // Keep expanded when alternate payment (Klarna/Afterpay/Venmo/Paze) is selected AND user has address
        return !isEditViewFromURL && !this.shouldOpenPaymentSection() && !shouldShowAlternatePayment && (isNewUserFlow || hasNoPaymentOptionSelected);
    };

    // SHP-3342: Close payment section on mWeb when PayPal completes.
    // mWeb uses URL-based routing (isEditViewFromURL) instead of component state.
    // Detect PayPal incomplete → complete and navigate away from /payment to collapse the section.
    shouldClosePaymentSection = prevProps => {
        const { payPalPaymentGroup, isEditViewFromURL } = this.props;

        return (
            isEditViewFromURL &&
            !prevProps.payPalPaymentGroup?.isComplete && // PayPal was incomplete before
            payPalPaymentGroup?.isComplete // PayPal is now complete (user returned from PayPal auth)
        );
    };

    /**
     * Helper to check if user has no saved payment methods.
     * Used to determine navigation behavior on Cancel (L2 vs L1).
     */
    hasNoSavedPaymentMethods = () => {
        const { creditCardPaymentGroup, payPalPaymentGroup, paymentOptions } = this.props;

        const noCreditCards = !paymentOptions?.creditCards?.length;
        const noPayPal = !payPalPaymentGroup?.isComplete;
        const creditCardIncomplete = !creditCardPaymentGroup?.isComplete;

        return noCreditCards && noPayPal && creditCardIncomplete;
    };

    /**
     * SHP-2591: Determines if we should show the new user payment view
     * Shows when user has no saved payment method AND has a saved address
     * Applies to: SaD checkout, BOPIS checkout, Guest checkout
     */
    shouldShowNewUserPaymentView = () => {
        const {
            creditCardPaymentGroup, payPalPaymentGroup, hasSavedAddress, paymentOptions, orderDetails, pendingCreditCard
        } = this.props;
        const { openCreditCardForm } = this.state;

        // Check if user has no saved credit cards
        const hasNoSavedCreditCards = !paymentOptions?.creditCards || paymentOptions.creditCards.length === 0;

        // Check if user has no saved PayPal (not just available, but actually saved and complete)
        const hasNoSavedPayPal = !payPalPaymentGroup?.isComplete;

        // Check if credit card payment group is not complete
        const creditCardNotComplete = !creditCardPaymentGroup?.isComplete;

        // User has no saved payment method if all of the above are true
        const hasNoSavedPaymentMethod = hasNoSavedCreditCards && hasNoSavedPayPal && creditCardNotComplete;

        const hasNoAlternatePaymentSaved =
            FrictionlessUtils.getSavedAlternatePayments(paymentOptions, orderDetails, Boolean(pendingCreditCard))?.length === 0;

        // Show new user view when: no payment method AND has saved address AND no alternate payment selected
        return (
            hasNoSavedPaymentMethod &&
            hasSavedAddress &&
            !openCreditCardForm &&
            !this.state.shouldAutoExpandPayPal &&
            !this.props.alternateMethodSelected &&
            hasNoAlternatePaymentSaved
        );
    };

    handlePaymentSectionOpenLogic = () => {
        const { activeSection } = this.props;
        const shouldOpen = this.shouldOpenPaymentSection();

        if (shouldOpen && activeSection !== SECTION_NAMES.PAYMENT) {
            this.props.setActiveSection(SECTION_NAMES.PAYMENT);
            this.setAnalytics();
        }
    };

    handlePaymentCheckOnExitEditView = prevProps => {
        const {
            isEditViewFromURL, orderDetails, availablePaymentMethods, isAlternatePaymentMethodSelected, alternateMethodSelected
        } = this.props;

        const isExitingEditMode = !isEditViewFromURL && prevProps.isEditViewFromURL;

        // Only act on the actual edit-mode exit transition, not intermediate renders
        if (!isExitingEditMode) {
            return;
        }

        // Skip when exiting via closeEditState (save/cancel handlers already manage state).
        // This prevents a race condition where this.props.alternateMethodSelected is stale
        if (this.exitingViaCloseEditState) {
            this.exitingViaCloseEditState = false;

            return;
        }

        const shouldCheckPayment = isAlternatePaymentMethodSelected(orderDetails, availablePaymentMethods) && !alternateMethodSelected;
        const shouldSuppressAutoOpen = this.hasNoSavedPaymentMethods() && !alternateMethodSelected;

        const nextState = {};

        if (shouldSuppressAutoOpen && !this.state.suppressAutoOpenAfterExternalExit) {
            nextState.suppressAutoOpenAfterExternalExit = true;
        }

        if (this.state.openCreditCardForm) {
            nextState.openCreditCardForm = false;
        }

        if (Object.keys(nextState).length > 0) {
            this.setState(nextState);
        }

        if (shouldCheckPayment) {
            this.props.checkSelectedPayment({ orderDetails });
        }
    };

    handleEnterEditModeLogic = prevProps => {
        const { isEditViewFromURL, alternateMethodSelected, alternatePaymentName, hasSavedOrDefaultAlternatePayment } = this.props;
        const isEnteringEditMode = !prevProps.isEditViewFromURL && isEditViewFromURL;
        const isVenmoExpressSavedState = Venmo.isVenmoExpressFlow() && alternatePaymentName === PAYMENT_TYPES.PAY_VENMO;

        if (!isEnteringEditMode) {
            return;
        }

        if (isVenmoExpressSavedState && !this.didHandleVenmoExpressAutoClose) {
            this.didHandleVenmoExpressAutoClose = true;

            // Auto-close only for Venmo express saved landing.
            // Keep normal Change behavior for other alternate methods.
            this.closeEditState();

            return;
        }

        // If an alt payment (Klarna/Afterpay/etc.) is selected, edit mode can be:
        // 1) User clicked “Change” then allow edit mode, but reset isReady so CTA goes back to “Place Order”.
        // 2) Auto after address save then backend doesn’t know the Redux-only selection yet, so stay on L1.
        if (alternateMethodSelected && !hasSavedOrDefaultAlternatePayment) {
            if (this.userInitiatedEditMode) {
                this.userInitiatedEditMode = false;
                this.props.commonOrderToggleActions?.(false, false, false, false);
                this.props.klarnaSetReady?.(false);
                this.props.afterpaySetReady?.(false);

                return;
            }

            this.closeEditState();

            return;
        }

        if (!hasSavedOrDefaultAlternatePayment) {
            this.props.commonOrderToggleActions?.(false, false, false, false);
            this.props.klarnaSetReady?.(false);
            this.props.afterpaySetReady?.(false);
        }
    };

    handleNewUserFlowChange = prevProps => {
        const {
            isEditViewFromURL, isNewUserFlow, tmpCardMessage, orderDetails, activeSection
        } = this.props;

        if (isEditViewFromURL && prevProps.isNewUserFlow && !isNewUserFlow) {
            this.setAnalytics();
        }

        if (prevProps.orderDetails !== orderDetails && tmpCardMessage && activeSection === SECTION_NAMES.PAYMENT) {
            this.loadTempCardMessageModal(tmpCardMessage);
        }
    };

    handlePaymentNeedsConfirmationState = prevProps => {
        const { isEditViewFromURL, paymentSectionNeedsConfirmation } = this.props;

        if (isEditViewFromURL !== prevProps.isEditViewFromURL && paymentSectionNeedsConfirmation !== isEditViewFromURL) {
            this.props.setPaymentMethodNeedsConfirmation(isEditViewFromURL);
        }
    };

    checkForCVVError = prevProps => {
        const { orderDetails } = this.props;

        const havePaymentGroupsChanged = !deepEqual(prevProps.orderDetails.header.paymentGroups, orderDetails.header.paymentGroups);

        if (havePaymentGroupsChanged && FrictionlessUtils.isCVVMissing(orderDetails.header.paymentGroups)) {
            this.savePendingCreditCard(orderDetails);
        }
    };

    updatePendingCreditCard = () => {
        const { pendingCreditCard, orderDetails } = this.props;

        if (!pendingCreditCard) {
            return;
        }

        const creditCardFromPaymentOptions = this.props.paymentOptions?.creditCards?.filter(
            cc => cc.creditCardId === pendingCreditCard?.creditCardId
        )?.[0];

        if (!deepEqual(pendingCreditCard, creditCardFromPaymentOptions)) {
            this.savePendingCreditCard(orderDetails);
        }
    };

    componentDidUpdate(prevProps) {
        this.handlePaymentSectionOpenLogic();
        this.handlePaymentCheckOnExitEditView(prevProps);
        this.handleEnterEditModeLogic(prevProps);

        if (this.shouldClosePaymentSection(prevProps)) {
            this.closeEditState();
        }

        this.handleNewUserFlowChange(prevProps);
        this.checkSelectedPayment(prevProps);
        this.validateGiftCardAmountNotEnoughMessage(prevProps);
        this.handlePaymentNeedsConfirmationState(prevProps);
        this.checkForCVVError(prevProps);
        this.updatePendingCreditCard();
    }

    checkSelectedPayment(prevProps) {
        const {
            isNewUserFlow, alternatePaymentName, hasSavedAddress, orderDetails, alternateMethodSelected
        } = this.props;

        if (!this.isNewUserWithVenmoExpress) {
            this.isNewUserWithVenmoExpress = !!(isNewUserFlow && alternatePaymentName === PAYMENT_TYPES.PAY_VENMO && Venmo.isVenmoExpressFlow());
        }

        const orderChanged = prevProps.orderDetails !== orderDetails;
        const justGotAddress = !prevProps.hasSavedAddress && hasSavedAddress;

        // Skip if alternate payment already selected locally—backend won't reflect it yet
        if (orderChanged && (this.isNewUserWithVenmoExpress || justGotAddress) && !alternateMethodSelected) {
            this.props.checkSelectedPayment({ orderDetails });
        }
    }

    setAnalytics = () => {
        const { zeroDollarHigherRedeemPointsNotCVV, paymentOptions, isNewUserFlow } = this.props;
        const openCreditCardForm = zeroDollarHigherRedeemPointsNotCVV && !paymentOptions?.creditCards?.length;
        const pageDetail = openCreditCardForm ? anaConsts.PAGE_NAMES.ADD_CREDIT_CARD : anaConsts.PAGE_TYPES.PAYMENT;
        !isNewUserFlow && FrictionlessCheckoutBindings.setChangeLinkAnalytics(pageDetail);
    };

    validateGiftCardAmountNotEnoughMessage = prevProps => {
        const {
            gcAmountNotEnoughMessage,
            alternateMethodSelected,
            orderDetails,
            giftCardAmountEqualsOrderTotal,
            isEditViewFromURL,
            isSubscriptionOrder,
            hasNoSavedCreditCards
        } = this.props;

        if (prevProps.orderDetails !== orderDetails && gcAmountNotEnoughMessage) {
            this.setGiftCardAmountNotEnoughMessage();
        }

        // Clear Payment section error when the gift card amount warning is removed after order refresh.
        if (prevProps.orderDetails !== orderDetails && prevProps.gcAmountNotEnoughMessage && !gcAmountNotEnoughMessage) {
            this.props.clearNamedSectionErrors([SECTION_NAMES.PAYMENT]);
        }

        const shouldClearSectionErrors = prevProps.gcAmountNotEnoughMessage && alternateMethodSelected;

        if (prevProps.orderDetails !== orderDetails && shouldClearSectionErrors) {
            this.props.clearCheckoutSectionErrors();
        }

        if (
            prevProps.giftCardAmountEqualsOrderTotal !== giftCardAmountEqualsOrderTotal &&
            giftCardAmountEqualsOrderTotal &&
            this.props.alternatePaymentName === PAYMENT_TYPES.PAY_VENMO
        ) {
            this.props.commonOrderToggleActions();
        }

        const shouldStayInEditMode = isSubscriptionOrder && hasNoSavedCreditCards;

        if (
            prevProps.giftCardAmountEqualsOrderTotal !== giftCardAmountEqualsOrderTotal &&
            giftCardAmountEqualsOrderTotal &&
            isEditViewFromURL &&
            !shouldStayInEditMode
        ) {
            this.closeEditState();
        }
    };

    renderEditView = () => {
        const {
            localization,
            giftCardPaymentGroups,
            shouldRenderGiftCardSection,
            availablePaymentMethods,
            alternateMethodSelected,
            alternatePaymentName,
            showErrorIcon,
            isGuestCheckout,
            zeroDollarHigherRedeemPointsCVV,
            zeroDollarHigherRedeemPointsNotCVV,
            paymentOptions,
            storeCredits,
            isPaymentComplete,
            orderHasPhysicalGiftCard,
            gcAmountNotEnoughMessage,
            isPhysicalGiftCardOnly,
            installmentValue,
            giftCardAmountEqualsOrderTotal,
            isSubscriptionOrder,
            hasNoSavedCreditCards,
            sectionLevelError
        } = this.props;

        // L3 State: AfterPay Expanded View
        // This is rendered at the Payment level to work for both new users and existing users
        if (this.state.isAfterPayExpanded) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <AfterPayExpandedView
                        afterpayApiData={this.state.afterpayApiData}
                        onSaveAndContinue={this.handleAfterPaySaveAndContinue}
                        onCancel={this.handleAfterPayCancel}
                        loadWidget={this.loadAfterPayWidget}
                        afterpayHeader={localization.afterpayHeader}
                        afterpaySubtitle={localization.afterpaySubtitle}
                        afterpaySaveAndContinue={localization.afterpaySaveAndContinue}
                        afterpayCancel={localization.afterpayCancel}
                        afterpayLoading={localization.afterpayLoading}
                        afterpayApiErrorFallback={localization.afterpayApiErrorFallback}
                        afterpayGiftCardWarning={localization.afterpayGiftCardWarning}
                        afterpayLegalNotice={localization.afterpayLegalNotice}
                        isLoading={this.state.isAfterPayLoading}
                        errorMessage={this.state.afterpayError}
                        installmentValue={installmentValue}
                        isAnonymous={isGuestCheckout}
                    />
                </Box>
            );
        }

        if (this.state.shouldAutoExpandVenmo) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <VenmoExpandedView
                        venmoHeader={localization.venmoHeader}
                        venmoSaveAndContinue={localization.venmoSaveAndContinue}
                        venmoCancel={localization.cancel}
                        onSaveAndContinue={this.handleVenmoSaveAndContinue}
                        onCancel={this.clearAutoExpandVenmo}
                        isAnonymous={isGuestCheckout}
                    />
                </Box>
            );
        }

        // L3 State: Paze Expanded View
        // Handles Paze expansion for new users (no saved payment methods)
        // where shouldShowNewUserPaymentView() would otherwise prevent PaymentMethodList from rendering
        if (this.state.shouldAutoExpandPaze) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <PazeExpandedView
                        pazeHeader={localization.pazeHeader}
                        pazeSubtitle={localization.pazeSubtitle}
                        pazeGiftCardWarning={localization.pazeGiftCardWarning}
                        pazeSaveAndContinue={localization.pazeSaveAndContinue}
                        pazeCancel={localization.pazeCancel}
                        onSaveAndContinue={this.handlePazeSaveAndContinue}
                        onCancel={this.handlePazeCancel}
                    />
                </Box>
            );
        }

        // L3 State: Klarna Expanded View
        if (this.state.isKlarnaExpanded) {
            const { orderDetails } = this.props;
            const isAnonymous = checkoutUtils.isGuestOrder();
            const isBopisOrder = orderDetails?.header?.isBopisOrder;

            return (
                <Box paddingX={[4, 4, 5]}>
                    <KlarnaExpandedView
                        klarnaApiData={this.state.klarnaApiData}
                        onSaveAndContinue={this.handleKlarnaSaveAndContinue}
                        onCancel={this.handleKlarnaCancel}
                        loadWidget={this.loadKlarnaWidget}
                        klarnaHeader={localization.klarnaHeader}
                        klarnaSubtitle={localization.klarnaSubtitle}
                        klarnaSaveAndContinue={localization.klarnaSaveAndContinue}
                        klarnaCancel={localization.klarnaCancel}
                        klarnaLoading={localization.klarnaLoading}
                        klarnaApiErrorFallback={localization.klarnaApiErrorFallback}
                        klarnaGiftCardWarning={localization.klarnaGiftCardWarning}
                        klarnaFeatureBuyerProtection={localization.klarnaFeatureBuyerProtection}
                        klarnaTermsText={localization.klarnaTermsText}
                        klarnaBillingAddressCheckbox={localization.klarnaBillingAddressCheckbox}
                        isLoading={this.state.isKlarnaLoading}
                        errorMessage={this.state.klarnaError}
                        installmentValue={installmentValue}
                        isAnonymous={isAnonymous}
                        isBopisOrder={isBopisOrder}
                        useMyShippingAddress={this.state.useMyShippingAddress}
                        onBillingAddressToggle={this.handleBillingAddressToggle}
                    />
                </Box>
            );
        }

        const shouldShowNewUserView = this.shouldShowNewUserPaymentView();
        const shouldRestrictAlternatePaymentsForFullCoverGC = giftCardAmountEqualsOrderTotal && !isSubscriptionOrder;
        const newUserAvailablePaymentMethods = shouldRestrictAlternatePaymentsForFullCoverGC
            ? {
                ...availablePaymentMethods,
                isKlarnaEnabledForThisOrder: false,
                isAfterpayEnabledForThisOrder: false,
                isAfterpayEnabledForThisProfile: false,
                isPazeEnabledForThisOrder: false
            }
            : availablePaymentMethods;

        if (shouldShowNewUserView) {
            return (
                <NewUserPaymentView
                    localization={localization}
                    availablePaymentMethods={newUserAvailablePaymentMethods}
                    paymentOptions={paymentOptions}
                    orderDetails={this.props.orderDetails}
                    installmentValue={this.props.installmentValue}
                    onAddNewCard={this.handleAddNewCard}
                    handleAfterPayExpand={this.handleAfterPayExpandFromL1}
                    handleKlarnaExpand={this.handleKlarnaExpandFromL1}
                    handlePayPalClick={this.handlePayPalExpandFromL1}
                    handleVenmoExpand={this.handleVenmoExpandFromL1}
                    handlePazeExpand={this.handlePazeExpandFromL1}
                    handlePayWithApplePayClick={this.handlePayWithApplePayClick}
                    giftCardPaymentGroups={giftCardPaymentGroups}
                    showGiftCardAdditionalPaymentRequired={!!gcAmountNotEnoughMessage}
                    giftCardAmountEqualsOrderTotal={giftCardAmountEqualsOrderTotal}
                    onGiftCardSaveAndContinue={this.closeEditState}
                    isGuestCheckout={isGuestCheckout}
                    isGiftCardOnly={this.props.isGiftCardOnly}
                    onAlternatePaymentDetails={this.handleAlternatePaymentDetails}
                    isSubscriptionOrder={isSubscriptionOrder}
                    hasNoSavedCreditCards={hasNoSavedCreditCards}
                    paymentSectionHasError={!!sectionLevelError}
                />
            );
        }

        // Show credit card form when user clicks Add New Card
        if (this.state.openCreditCardForm) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <CreditCardArea
                        handleCloseForm={this.handleCloseCreditCardForm}
                        callback={this.handleNewUserCreditCardSave}
                        isGuestCheckout={isGuestCheckout}
                        hideCancelButton={false}
                        editCreditCard={false}
                        isEditCCFlow={false}
                    />
                </Box>
            );
        }

        return (
            <Box paddingX={[4, 4, 5]}>
                <PaymentMethodList
                    {...availablePaymentMethods}
                    setEditChangeLink={this.setEditChangeLink}
                    alternateMethodSelected={alternateMethodSelected}
                    alternatePaymentName={alternatePaymentName}
                    showGiftCard={giftCardPaymentGroups.length < 2 && shouldRenderGiftCardSection}
                    showEditButtonFirst={showErrorIcon}
                    saveBtnSuccessCallback={this.closeEditState}
                    isGuestCheckout={isGuestCheckout}
                    zeroDollarHigherRedeemPointsCVV={zeroDollarHigherRedeemPointsCVV}
                    openCreditCardForm={zeroDollarHigherRedeemPointsNotCVV && !paymentOptions?.creditCards?.length}
                    storeCredits={storeCredits}
                    isPaymentComplete={isPaymentComplete}
                    orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                    showGCAmountNotEnoughMessage={!!gcAmountNotEnoughMessage}
                    isPhysicalGiftCardOnly={isPhysicalGiftCardOnly}
                    giftCardAmountEqualsOrderTotal={giftCardAmountEqualsOrderTotal}
                    shouldAutoExpandAfterpay={this.state.shouldAutoExpandAfterpay}
                    clearAutoExpandAfterpay={this.clearAutoExpandAfterpay}
                    installmentValue={this.props.installmentValue}
                    paymentLocales={localization}
                    shouldAutoExpandKlarna={this.state.shouldAutoExpandKlarna}
                    clearAutoExpandKlarna={this.clearAutoExpandKlarna}
                    shouldAutoExpandPayPal={this.state.shouldAutoExpandPayPal}
                    clearAutoExpandPayPal={this.clearAutoExpandPayPal}
                    shouldAutoExpandPaze={this.state.shouldAutoExpandPaze}
                    clearAutoExpandPaze={this.clearAutoExpandPaze}
                    clearPendingCreditCard={this.props.clearPendingCreditCard}
                    isSubscriptionOrder={isSubscriptionOrder}
                    hasNoSavedCreditCards={hasNoSavedCreditCards}
                    onAlternatePaymentDetails={this.handleAlternatePaymentDetails}
                />
            </Box>
        );
    };

    /* eslint-disable-next-line complexity */
    render() {
        const {
            sectionNumber,
            localization,
            isPaymentComplete,
            creditCardPaymentGroup,
            giftCardPaymentGroups,
            availablePaymentMethods,
            isPaypalSelected,
            payPalPaymentGroup,
            alternateMethodSelected,
            isNewUserFlow,
            storeCredits,
            hasGiftCardInOrder,
            alternatePaymentName,
            installmentValue,
            isCombinableGiftCards,
            showErrorIcon,
            isGuestCheckout,
            venmoUsername,
            sectionLevelError,
            isEfullfillmentOrder,
            isPhysicalGiftCardAndHardGoodShippingGroup,
            isEditViewFromURL,
            orderDetails,
            gcAmountNotEnoughMessage,
            giftCardAmountEqualsOrderTotal,
            isSubscriptionOrder,
            hasNoSavedCreditCards
        } = this.props;

        const { isGiftCardFormOpenOnL1, openCreditCardForm } = this.state;

        const {
            isAfterpayEnabledForThisOrder,
            isKlarnaEnabledForThisOrder,
            isVenmoEnabledForThisOrder,
            isPazeEnabledForThisOrder,
            isPayPalEnabled,
            isPayPalPayLaterEligible,
            isAfterpayEnabledForThisProfile
        } = availablePaymentMethods;
        const orderAmount = orderDetails?.priceInfo?.creditCardAmount || orderDetails?.priceInfo?.paypalAmount || 0;
        const isPaymentSectionCollapsed = this.shouldCollapsePaymentSection();
        const shouldRenderInlineGiftCardAdditionalPaymentWarning = sectionLevelError && sectionLevelError === gcAmountNotEnoughMessage;
        const isSubscriptionNoSavedCard = isSubscriptionOrder && hasNoSavedCreditCards;
        const shouldRenderHeaderErrorIconOnly = isSubscriptionNoSavedCard && !openCreditCardForm;
        const shouldHidePaymentSectionError =
            shouldRenderInlineGiftCardAdditionalPaymentWarning ||
            isSubscriptionNoSavedCard;
        const effectiveSectionLevelError = shouldHidePaymentSectionError ? null : sectionLevelError;

        return (
            <LayoutCard
                sectionInfo={{
                    sectionNumber:
                        (showErrorIcon && !isNewUserFlow && !isGuestCheckout && !openCreditCardForm) ||
                        shouldRenderHeaderErrorIconOnly ||
                        (effectiveSectionLevelError && !shouldRenderInlineGiftCardAdditionalPaymentWarning)
                            ? null
                            : sectionNumber,
                    title: localization.cardTitle,
                    hasDivider: true,
                    isChangePermitted: !isEditViewFromURL && !isGiftCardFormOpenOnL1,
                    onChangeClick: this.setEditChangeLink,
                    renderErrorView:
                        (showErrorIcon && !isNewUserFlow && !isGuestCheckout && !openCreditCardForm) ||
                        shouldRenderHeaderErrorIconOnly ||
                        (!!effectiveSectionLevelError && !shouldRenderInlineGiftCardAdditionalPaymentWarning),
                    removeMarginTop: isEditViewFromURL,
                    sectionLevelError: effectiveSectionLevelError,
                    sectionName: SECTION_NAMES.PAYMENT
                }}
                marginTop={[4, 4, 5]}
                hasPaddingForChildren={false}
                isEditMode={isEditViewFromURL}
                isCollapsed={isPaymentSectionCollapsed}
                ariaLabel={localization.paymentInformation}
                role='region'
                sectionIcon={
                    <Flex flexDirection='row'>
                        <Icon
                            name='lock'
                            size='1.25em'
                            marginRight='.4em'
                            aria-hidden='true'
                        />
                        <Text color='gray'>{localization.secure}</Text>
                    </Flex>
                }
            >
                {/* Hidden container for legacy Klarna background initialization (old modal flow) */}
                {/* This prevents "InvalidContainerSelectorError" when commonOrderToggleActions dispatches backgroundInit */}
                <div
                    id='backgroundPaymentWidget'
                    style={{ display: 'none' }}
                />

                {/* Render L1 (collapsed/saved state) when not editing */}
                {/* Show PaymentsDisplay when: not in edit mode */}
                {/* Important: alternateMethodSelected keeps section expanded after Afterpay Save & Continue */}
                {!isEditViewFromURL ? (
                    <PaymentsDisplay
                        isPaymentComplete={isPaymentComplete}
                        creditCardPaymentGroup={creditCardPaymentGroup}
                        isPaypalSelected={isPaypalSelected}
                        payPalPaymentGroup={payPalPaymentGroup}
                        venmoUsername={venmoUsername}
                        storeCredits={storeCredits}
                        giftCardPaymentGroups={giftCardPaymentGroups}
                        hasGiftCardInOrder={hasGiftCardInOrder || !isCombinableGiftCards}
                        localization={localization}
                        alternatePaymentName={alternatePaymentName}
                        alternateMethodSelected={alternateMethodSelected}
                        checkoutEnabled={
                            isAfterpayEnabledForThisOrder || isKlarnaEnabledForThisOrder || isVenmoEnabledForThisOrder || isPazeEnabledForThisOrder
                        }
                        installmentValue={installmentValue}
                        showError={showErrorIcon}
                        isEfullfillmentOrder={isEfullfillmentOrder}
                        shouldRenderGiftCardSection={isPhysicalGiftCardAndHardGoodShippingGroup}
                        orderAmount={orderAmount}
                        isAfterpayEnabledForThisOrder={isAfterpayEnabledForThisOrder}
                        isKlarnaEnabledForThisOrder={isKlarnaEnabledForThisOrder}
                        isPayPalEnabled={isPayPalEnabled}
                        isPayPalPayLaterEligible={isPayPalPayLaterEligible}
                        isAfterpayEnabledForThisProfile={isAfterpayEnabledForThisProfile}
                        isPazeEnabledForThisOrder={isPazeEnabledForThisOrder}
                        handleAfterPayExpand={this.handleAfterPayExpandFromL1}
                        handleKlarnaExpand={this.handleKlarnaExpandFromL1}
                        handlePayPalClick={this.handlePayPalExpandFromL1}
                        handlePazeExpand={this.handlePazeExpandFromL1}
                        onGiftCardFormVisibilityChange={this.handleGiftCardFormVisibilityOnL1}
                        isGiftCardFormOpen={isGiftCardFormOpenOnL1}
                        showGiftCardAdditionalPaymentRequired={!!gcAmountNotEnoughMessage}
                        giftCardAmountEqualsOrderTotal={giftCardAmountEqualsOrderTotal}
                        isGuestCheckout={isGuestCheckout}
                        isSubscriptionOrder={isSubscriptionOrder}
                    />
                ) : (
                    this.renderEditView()
                )}
            </LayoutCard>
        );
    }
}

export default wrapComponent(Payment, 'Payment', true);
